// ── Boot sequence (Phase 2D) ──
// Implements AR's organic-emergence brief (May 4 2026): user lands and
// everything is underexposed except the orange box on the wordmark "i" dot,
// which gives birth to a second orange square that travels next to "Cognitive
// Partner" (the hero eyebrow).
//
// Five sub-beats per the ChatGPT review AR locked:
//   pressure (0–200ms)    parent gathers force; child not yet visible
//   bud      (200–550ms)  child grows from parent's surface, slight outward
//                         displacement; tether is the parent's surface itself
//   tether   (550–700ms)  child stretches outward, tether thins
//   snap     (700–820ms)  release — child accelerates outward, tether breaks,
//                         brightness spike at the separation point
//   drift    (820–1400ms) elastic settle to the eyebrow position
//
// Parent-side animation lives in CSS on `.brand-i-dot` (keyframe
// `wordmark-dot-birth`); this file owns the child + tether render in the
// boot-canvas (z-99, above the underexposure veil at z-95).

import type { LogoAnchor } from './glass-renderer.js';
import { lerp, smoothstep } from '../lib/easing.js';

const T_PRESSURE_END = 200;
const T_BUD_END = 550;
const T_TETHER_END = 700;
const T_SNAP_END = 820;
const T_DRIFT_END = 1400;

const COPPER_R = 0xff;
const COPPER_G = 0x82;
const COPPER_B = 0x4c;

export class BootSequence {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr: number = 1;
  private startMs: number = 0;
  private parent: LogoAnchor = { x: 0, y: 0, size: 8 };
  private target: LogoAnchor = { x: 0, y: 0, size: 10 };
  private done: boolean = false;
  private running: boolean = false;

  constructor(canvasEl: HTMLCanvasElement) {
    this.canvas = canvasEl;
    const ctx = canvasEl.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  private resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  start(parent: LogoAnchor, target: LogoAnchor, nowMs: number): void {
    this.parent = { ...parent };
    this.target = { ...target };
    this.startMs = nowMs;
    this.running = true;
    this.done = false;
  }

  isDone(): boolean { return this.done; }
  isRunning(): boolean { return this.running && !this.done; }

  /** Skip immediately to settled state (reduced motion / mid-scroll load). */
  skip(): void {
    this.done = true;
    this.running = false;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.ctx.clearRect(0, 0, w, h);
    // Clear the veil driver so the boot-complete CSS rule transitions cleanly
    // from whatever opacity the veil was last at.
    document.documentElement.style.removeProperty('--veil-opacity');
  }

  draw(nowMs: number): void {
    if (!this.running || this.done) return;

    const t = nowMs - this.startMs;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.ctx.clearRect(0, 0, w, h);

    if (t >= T_DRIFT_END) {
      this.done = true;
      this.running = false;
      return;
    }

    // ── Phase computation ──────────────────────────────────────
    let childAlpha = 0;
    let childScale = 0;
    let childX = this.parent.x;
    let childY = this.parent.y;
    let aspectX = 1; // child squash/stretch in x
    let aspectY = 1; // and y
    let tetherAlpha = 0;
    let tetherWidth = 1.5;
    let snapFlash = 0;

    if (t < T_PRESSURE_END) {
      // Pressure (0–200ms): child invisible, parent does its CSS bulge.
      // No render output here.
    } else if (t < T_BUD_END) {
      // Bud (200–550ms): child grows from 0 → 0.85, barely moves outward.
      // Aspect stays 1:1 (per AR — no squash/stretch in pressure or bud).
      const u = (t - T_PRESSURE_END) / (T_BUD_END - T_PRESSURE_END);
      const eased = smoothstep(0, 1, u);
      childAlpha = Math.min(1, eased * 1.6);
      childScale = 0.85 * eased;
      aspectX = 1;
      aspectY = 1;
      // 0 → 12% along the path (still close to parent)
      const travel = eased * 0.12;
      childX = lerp(this.parent.x, this.target.x, travel);
      childY = lerp(this.parent.y, this.target.y, travel);
      tetherAlpha = 0.85 * eased;
      tetherWidth = 1.6 + (1 - eased) * 0.8; // thicker near the parent
    } else if (t < T_TETHER_END) {
      // Tether (550–700ms): child reaches scale 1.0, position stretches outward.
      const u = (t - T_BUD_END) / (T_TETHER_END - T_BUD_END);
      const eased = smoothstep(0, 1, u);
      childAlpha = 1;
      childScale = 0.85 + 0.15 * eased;
      aspectX = 0.92 + 0.06 * eased;
      aspectY = 1.10 - 0.06 * eased;
      const travel = 0.12 + eased * 0.46; // 12% → 58%
      childX = lerp(this.parent.x, this.target.x, travel);
      childY = lerp(this.parent.y, this.target.y, travel);
      tetherAlpha = 0.85 * (1 - eased * 0.5);
      tetherWidth = 1.5 - eased * 0.7; // thins
    } else if (t < T_SNAP_END) {
      // Snap (700–820ms): rapid acceleration, overshoot to ~115%, tether breaks.
      const u = (t - T_TETHER_END) / (T_SNAP_END - T_TETHER_END);
      const eased = u * u; // accelerating
      childAlpha = 1;
      childScale = 1.0 + 0.10 * eased;
      aspectX = 1.06;
      aspectY = 0.96;
      const travel = 0.58 + eased * 0.57; // 58% → 115% (overshoot)
      childX = lerp(this.parent.x, this.target.x, travel);
      childY = lerp(this.parent.y, this.target.y, travel);
      tetherAlpha = 0.4 * (1 - u);
      tetherWidth = 0.8;
      snapFlash = (1 - u) * (1 - u); // peaks at start of snap, decays
    } else {
      // Drift (820–1400ms): elastic settle to target. Child overshoots then
      // wobbles back; scale eases from 1.10 → 1.0.
      const u = (t - T_SNAP_END) / (T_DRIFT_END - T_SNAP_END);
      childAlpha = 1;
      childScale = 1.10 - 0.10 * smoothstep(0, 1, u);
      aspectX = 1 + 0.04 * Math.exp(-u * 4) * Math.cos(u * 12);
      aspectY = 1 - 0.04 * Math.exp(-u * 4) * Math.cos(u * 12);
      // Damped oscillation around 100%
      const overshoot = 1 + 0.12 * Math.exp(-u * 5) * Math.cos(u * 9);
      childX = lerp(this.parent.x, this.target.x, overshoot);
      childY = lerp(this.parent.y, this.target.y, overshoot);
      tetherAlpha = 0;
    }

    // ── Tether render (parent → child, with curve toward midpoint) ──
    if (tetherAlpha > 0.01) {
      const grad = this.ctx.createLinearGradient(
        this.parent.x, this.parent.y,
        childX, childY,
      );
      grad.addColorStop(0, `rgba(${COPPER_R}, ${COPPER_G}, ${COPPER_B}, ${tetherAlpha})`);
      grad.addColorStop(1, `rgba(${COPPER_R}, ${COPPER_G}, ${COPPER_B}, ${tetherAlpha * 0.4})`);
      this.ctx.strokeStyle = grad;
      this.ctx.lineWidth = tetherWidth;
      this.ctx.lineCap = 'round';
      this.ctx.beginPath();
      this.ctx.moveTo(this.parent.x, this.parent.y);
      // Slight curve via quadratic toward midpoint, droops a touch (umbilical).
      const midX = (this.parent.x + childX) / 2;
      const midY = (this.parent.y + childY) / 2 + Math.min(8, Math.abs(childX - this.parent.x) * 0.05);
      this.ctx.quadraticCurveTo(midX, midY, childX, childY);
      this.ctx.stroke();
    }

    // ── Snap flash at separation point (decaying glow at parent surface) ──
    if (snapFlash > 0.01) {
      const flashR = 14 + 10 * snapFlash;
      const flash = this.ctx.createRadialGradient(
        this.parent.x, this.parent.y, 0,
        this.parent.x, this.parent.y, flashR,
      );
      flash.addColorStop(0, `rgba(${COPPER_R}, ${COPPER_G}, ${COPPER_B}, ${snapFlash * 0.55})`);
      flash.addColorStop(1, `rgba(${COPPER_R}, ${COPPER_G}, ${COPPER_B}, 0)`);
      this.ctx.fillStyle = flash;
      this.ctx.fillRect(this.parent.x - flashR, this.parent.y - flashR, flashR * 2, flashR * 2);
    }

    // ── Child square ──
    if (childAlpha > 0.01 && childScale > 0.01) {
      const baseSize = this.target.size;
      const sx = baseSize * childScale * aspectX;
      const sy = baseSize * childScale * aspectY;
      this.ctx.fillStyle = `rgba(${COPPER_R}, ${COPPER_G}, ${COPPER_B}, ${childAlpha})`;
      this.ctx.fillRect(childX - sx / 2, childY - sy / 2, sx, sy);
    }

    // ── Veil opacity (drives `--veil-opacity` CSS var) ──
    // Pressure: 0.70 (30% brightness, full underexposure, only Mark visible).
    // Bud onward: as soon as Bit begins moving from Mark, Veil starts lifting,
    // ramping 0.70 → 0 keyed on Bit's travel distance toward the eyebrow.
    // Exposure follows the protagonist.
    let veilOpacity = 0.70;
    if (t >= T_PRESSURE_END) {
      const totalDist = Math.hypot(
        this.target.x - this.parent.x,
        this.target.y - this.parent.y,
      );
      const travelDist = Math.hypot(
        childX - this.parent.x,
        childY - this.parent.y,
      );
      const travel = totalDist > 0 ? Math.min(1, travelDist / totalDist) : 0;
      veilOpacity = 0.70 * (1 - travel);
    }
    document.documentElement.style.setProperty(
      '--veil-opacity',
      veilOpacity.toFixed(3),
    );
  }
}
